// =====================================================================
// Mon arbre
// =====================================================================
// L'onglet est une pile d'écrans, chacun occupant TOUTE la page :
//
//   arbre   l'arbre dessiné, plein écran — c'est l'écran principal
//   liste   les mêmes personnes groupées par génération
//   menu    « Compléter mon arbre », les quatre déclarations possibles
//   <ajout> le formulaire de la déclaration choisie
//
// L'utilisateur ne déclare que ses proches directs : parents,
// grands-parents, fratrie, enfants. Oncles, tantes, cousins, neveux et
// arrière-grands-parents sont calculés par la base, jamais saisis.
// =====================================================================

import { useEffect, useMemo, useState } from 'react';
import { api, ErreurApi } from '../api';
import type { Identite, Lien, Moi, Parent, RoleParent } from '../../partage/types';
import { VueArbre } from '../composants/VueArbre';
import { FichePersonne } from '../composants/FichePersonne';
import {
  Alerte,
  Chargement,
  ChoixRole,
  FormulaireIdentite,
  IconeArbre,
  identiteValide,
  nomComplet,
  useFileAttente,
} from '../composants/base';
import {
  personnesAttendues,
  liensAttendus,
  type Attendu,
} from '../attente-arbre';
import { retirer } from '../file-attente';
import { Avatar, trancheDepuisGeneration } from '../composants/Avatar';

type Ajout = 'parent' | 'grand-parent' | 'fratrie' | 'enfant';
type Ecran = 'arbre' | 'liste' | 'menu' | Ajout;

const TITRE_AJOUT: Record<Ajout, string> = {
  parent: 'Ajouter mon père ou ma mère',
  'grand-parent': 'Ajouter un grand-parent',
  fratrie: 'Ajouter mon frère ou ma sœur',
  enfant: 'Ajouter mon enfant',
};

const DETAIL_AJOUT: Record<Ajout, string> = {
  parent: 'La première chose à déclarer : tout part de là.',
  'grand-parent': "C'est par eux que Yambi retrouve les autres branches.",
  fratrie: 'Même père et même mère, ou un seul parent en commun.',
  enfant: 'Vos fils et vos filles.',
};

/**
 * Regroupe les parents, du plus ancien au plus récent.
 *
 * Le regroupement suit EXACTEMENT la même règle que le libellé calculé par
 * la base : pour la parenté collatérale, c'est l'écart de génération
 * (dx − dy) qui décide, pas le degré de cousinage. Sans ça les deux se
 * contredisent — les enfants de ma cousine s'affichaient « neveu » tout en
 * étant rangés sous « Cousins et cousines ».
 */
function grouper(liste: Parent[]): [string, Parent[]][] {
  const groupes = new Map<string, Parent[]>();

  for (const p of liste) {
    const dx = p.profondeur_moi;
    const dy = p.profondeur_autre;
    const ecart = dx - dy;
    let cle: string;

    if (dy === 0) {
      // Ascendance directe
      cle =
        dx >= 3 ? 'Arrière-grands-parents' : dx === 2 ? 'Grands-parents' : 'Parents';
    } else if (dx === 0) {
      // Descendance directe
      cle =
        dy >= 3
          ? 'Arrière-petits-enfants'
          : dy === 2
            ? 'Petits-enfants'
            : 'Mes enfants';
    } else if (ecart === 0) {
      cle = dx === 1 ? 'Frères et sœurs' : 'Cousins et cousines';
    } else if (ecart === -1) {
      cle = 'Neveux et nièces';
    } else if (ecart === -2) {
      cle = 'Petits-neveux et petites-nièces';
    } else if (ecart === 1) {
      cle = 'Oncles et tantes';
    } else if (ecart === 2) {
      cle = 'Grands-oncles et grands-tantes';
    } else {
      cle = 'Parenté éloignée';
    }

    (groupes.get(cle) ?? groupes.set(cle, []).get(cle)!).push(p);
  }

  const ordre = [
    'Arrière-grands-parents',
    'Grands-parents',
    'Grands-oncles et grands-tantes',
    'Parents',
    'Oncles et tantes',
    'Frères et sœurs',
    'Cousins et cousines',
    'Mes enfants',
    'Neveux et nièces',
    'Petits-enfants',
    'Petits-neveux et petites-nièces',
    'Arrière-petits-enfants',
    'Parenté éloignée',
  ];

  return [...groupes.entries()].sort(
    (a, b) => ordre.indexOf(a[0]) - ordre.indexOf(b[0]),
  );
}

export function Arbre({
  moi,
  onRafraichir,
  onPleinePage,
}: {
  moi: Moi;
  onRafraichir?: () => void;
  /** Prévient l'ossature que cet écran veut toute la page, sans marges. */
  onPleinePage?: (plein: boolean) => void;
}) {
  const [liste, setListe] = useState<Parent[] | null>(null);
  const [tronque, setTronque] = useState(false);
  const [liens, setLiens] = useState<Lien[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ecran, setEcran] = useState<Ecran>('arbre');
  const [fiche, setFiche] = useState<string | null>(null);

  async function charger() {
    try {
      const [arbre, aretes] = await Promise.all([api.arbre(), api.liens()]);
      setListe(arbre.personnes);
      setTronque(arbre.tronque);
      setLiens(aretes);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Chargement impossible.');
    }
  }

  useEffect(() => {
    void charger();
  }, []);

  // Ce qui a été saisi hors ligne est montré tout de suite, marqué « en
  // attente ». Sans cela on ne saurait plus qui on a déjà déclaré au
  // village, et on déclarerait deux fois.
  const file = useFileAttente();
  const attendus = useMemo(() => personnesAttendues(file), [file]);

  const listeComplete = useMemo(
    () => (liste === null ? null : [...liste, ...attendus]),
    [liste, attendus],
  );

  const liensComplets = useMemo(
    () => [
      ...liens,
      ...liensAttendus(file, moi.personne_id, moi.sexe, liste ?? [], liens),
    ],
    [file, liens, liste, moi.personne_id, moi.sexe],
  );

  const vide = listeComplete !== null && listeComplete.length === 0;
  const pleinePage = ecran === 'arbre' && listeComplete !== null && !vide;

  // Seul l'arbre dessiné réclame la page entière ; les autres écrans
  // gardent les marges de lecture.
  useEffect(() => {
    onPleinePage?.(pleinePage);
    return () => onPleinePage?.(false);
  }, [pleinePage, onPleinePage]);

  const groupes = useMemo(() => grouper(listeComplete ?? []), [listeComplete]);
  const choisie = liste?.find((p) => p.personne_id === fiche) ?? null;

  // --- Le formulaire de déclaration, sa propre page -------------------
  if (ecran !== 'arbre' && ecran !== 'liste' && ecran !== 'menu') {
    return (
      <FormulaireAjout
        type={ecran}
        onAnnuler={() => setEcran('menu')}
        onFait={() => {
          setEcran('arbre');
          void charger();
          onRafraichir?.();
        }}
      />
    );
  }

  // --- « Compléter mon arbre », sa propre page ------------------------
  if (ecran === 'menu') {
    return (
      <>
        <button className="bouton discret retour" onClick={() => setEcran('arbre')}>
          ← Mon arbre
        </button>

        <h2 className="titre-page">Compléter mon arbre</h2>
        <p className="doux" style={{ marginBottom: 20 }}>
          Déclarez seulement vos proches directs. Yambi en déduit les oncles,
          tantes, cousins et neveux, et rapproche les familles tout seul.
        </p>

        {(Object.keys(TITRE_AJOUT) as Ajout[]).map((t) => (
          <button key={t} className="choix-ajout" onClick={() => setEcran(t)}>
            <span className="choix-titre">{TITRE_AJOUT[t]}</span>
            <span className="choix-detail">{DETAIL_AJOUT[t]}</span>
          </button>
        ))}
      </>
    );
  }

  if (listeComplete === null) return <Chargement />;

  // --- Arbre vide ------------------------------------------------------
  if (vide) {
    return (
      <div className="vide">
        <IconeArbre />
        <p>Votre arbre est vide.</p>
        <p className="doux" style={{ marginBottom: 20 }}>
          Commencez par votre père ou votre mère : tout le reste en découle.
        </p>
        <button className="bouton" onClick={() => setEcran('menu')}>
          Compléter mon arbre
        </button>
        {erreur && <Alerte>{erreur}</Alerte>}
      </div>
    );
  }

  // --- Arbre et liste --------------------------------------------------
  const barre = (
    <div className="barre-arbre">
      <div className="bascule" role="tablist" aria-label="Affichage">
        <button
          role="tab"
          aria-selected={ecran === 'arbre'}
          onClick={() => setEcran('arbre')}
        >
          Arbre
        </button>
        <button
          role="tab"
          aria-selected={ecran === 'liste'}
          onClick={() => setEcran('liste')}
        >
          Liste
        </button>
      </div>
      <button className="bouton mince" onClick={() => setEcran('menu')}>
        + Ajouter
      </button>
    </div>
  );

  const ficheOuverte = choisie && (
    <FichePersonne
      personne={choisie}
      moi={moi}
      onFerme={() => setFiche(null)}
      onModifie={() => {
        void charger();
        onRafraichir?.();
      }}
    />
  );

  if (ecran === 'arbre') {
    return (
      <div className="page-arbre">
        {ficheOuverte}
        {barre}
        {erreur && (
          <div className="marge-laterale">
            <Alerte>{erreur}</Alerte>
          </div>
        )}
        {tronque && (
          <div className="marge-laterale">
            <Alerte>
              Votre famille dépasse ce que cet écran peut afficher d'un seul
              tenant. Les parents les plus éloignés ne sont pas montrés.
            </Alerte>
          </div>
        )}
        <VueArbre
          moi={moi}
          parents={listeComplete}
          liens={liensComplets}
          onChoisir={(id) => {
            if (id !== moi.personne_id) setFiche(id);
          }}
        />
      </div>
    );
  }

  return (
    <>
      {ficheOuverte}
      {barre}
      {erreur && <Alerte>{erreur}</Alerte>}
      {tronque && (
        <Alerte>
          Votre famille dépasse ce que cet écran peut afficher d'un seul
          tenant. Les parents les plus éloignés ne sont pas montrés.
        </Alerte>
      )}

      {groupes.map(([titre, membres]) => (
        <section key={titre}>
          <h3>
            {titre} <span className="doux">· {membres.length}</span>
          </h3>
          <div className="carte" style={{ padding: '4px 16px' }}>
            {membres.map((p) => (
              <LigneParent
                key={p.personne_id}
                parent={p}
                onChoisir={() => setFiche(p.personne_id)}
                onAbandonner={() => void retirer(p.personne_id)}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function LigneParent({
  parent,
  onChoisir,
  onAbandonner,
}: {
  parent: Parent;
  onChoisir: () => void;
  onAbandonner?: () => void;
}) {
  const sansNom = !parent.nom && !parent.prenoms;
  // Saisie hors ligne : la ligne existe chez nous, pas encore sur le
  // serveur. On le dit, sinon on la croirait enregistrée.
  const attendu = 'en_attente' in parent ? (parent as Attendu) : null;

  return (
    <div
      className="parent cliquable"
      onClick={attendu ? () => {} : onChoisir}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onChoisir();
      }}
    >
      <Avatar
        id={parent.personne_id}
        sexe={parent.sexe}
        tranche={trancheDepuisGeneration(
          parent.profondeur_moi - parent.profondeur_autre,
        )}
        inconnu={sansNom}
        taille={42}
      />
      <div className="corps">
        <div className="nom">
          {sansNom ? 'Nom inconnu' : nomComplet(parent.nom, parent.prenoms)}
          {parent.decede && <span className="doux"> · †</span>}
          {attendu && (
            <span
              className={`etiquette-attente${attendu.refus ? ' refus' : ''}`}
            >
              {attendu.refus ? 'refusé' : 'à envoyer'}
            </span>
          )}
        </div>
        <div className="relation">
          {parent.libelle}
          {parent.cote && parent.profondeur_moi >= 2 && (
            <span className="doux" style={{ fontWeight: 400 }}>
              {' '}
              ({parent.cote === 'pere' ? 'côté paternel' : 'côté maternel'})
            </span>
          )}
        </div>
        {parent.village_origine && (
          <div className="lieu">{parent.village_origine}</div>
        )}
        {attendu?.refus && (
          <>
            <div className="lieu" style={{ color: 'var(--alerte)' }}>
              {attendu.refus}
            </div>
            <button
              className="bouton danger mince"
              style={{ marginTop: 8 }}
              onClick={(e) => {
                e.stopPropagation();
                onAbandonner?.();
              }}
            >
              Abandonner cette déclaration
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Formulaire d'ajout
// =====================================================================

function FormulaireAjout({
  type,
  onAnnuler,
  onFait,
}: {
  type: Ajout;
  onAnnuler: () => void;
  onFait: () => void;
}) {
  const [identite, setIdentite] = useState<Identite>({});
  const [role, setRole] = useState<RoleParent | null>(null);
  const [cote, setCote] = useState<RoleParent | null>(null);
  const [memesParents, setMemesParents] = useState<boolean | null>(null);
  const [parentPartage, setParentPartage] = useState<RoleParent | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const pret =
    identiteValide(identite) &&
    (type === 'enfant' ||
      (type === 'parent' && role !== null) ||
      (type === 'grand-parent' && cote !== null && role !== null) ||
      (type === 'fratrie' &&
        memesParents !== null &&
        (memesParents || parentPartage !== null)));

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      switch (type) {
        case 'parent':
          await api.declarerParent(role!, identite);
          break;
        case 'grand-parent':
          await api.declarerGrandParent(cote!, role!, identite);
          break;
        case 'fratrie':
          await api.declarerFratrie(
            memesParents!,
            memesParents ? null : parentPartage,
            identite,
          );
          break;
        case 'enfant':
          await api.declarerEnfant(identite);
          break;
      }
      onFait();
    } catch (e) {
      setErreur(
        e instanceof ErreurApi ? e.message : 'Enregistrement impossible.',
      );
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <>
      <button className="bouton discret retour" onClick={onAnnuler} disabled={envoi}>
        ← Compléter mon arbre
      </button>

      <h2 className="titre-page">{TITRE_AJOUT[type]}</h2>

      <form onSubmit={soumettre} noValidate>
        {erreur && <Alerte>{erreur}</Alerte>}

        {type === 'parent' && (
          <ChoixRole
            legende="C'est votre…"
            valeur={role}
            onChange={setRole}
            nom="role-parent"
          />
        )}

        {type === 'grand-parent' && (
          <>
            <ChoixRole
              legende="Par qui remontez-vous ?"
              valeur={cote}
              onChange={setCote}
              libelles={{ pere: 'Par mon père', mere: 'Par ma mère' }}
              nom="cote"
            />
            <ChoixRole
              legende={
                cote === 'mere'
                  ? "C’est le père ou la mère de votre mère ?"
                  : "C’est le père ou la mère de votre père ?"
              }
              valeur={role}
              onChange={setRole}
              nom="role-grand-parent"
            />
            <p className="doux" style={{ marginTop: -5, marginBottom: 13 }}>
              Si vous n'avez pas encore déclaré ce parent, Yambi crée un
              emplacement sans nom pour lui. Vous pourrez le nommer plus tard.
            </p>
          </>
        )}

        {type === 'fratrie' && (
          <>
            <fieldset>
              <legend>Quel lien exactement ?</legend>
              <div className="radios">
                <label>
                  <input
                    type="radio"
                    name="memes-parents"
                    checked={memesParents === true}
                    onChange={() => setMemesParents(true)}
                  />
                  Même père et même mère
                </label>
                <label>
                  <input
                    type="radio"
                    name="memes-parents"
                    checked={memesParents === false}
                    onChange={() => setMemesParents(false)}
                  />
                  Un seul parent en commun
                </label>
              </div>
            </fieldset>

            {memesParents === false && (
              <ChoixRole
                legende="Lequel ?"
                valeur={parentPartage}
                onChange={setParentPartage}
                libelles={{ pere: 'Même père', mere: 'Même mère' }}
                nom="parent-partage"
              />
            )}
          </>
        )}

        <FormulaireIdentite
          valeur={identite}
          onChange={setIdentite}
          avecContact={type !== 'grand-parent'}
          avecDeces={type === 'parent' || type === 'grand-parent'}
        />

        <div className="rangee-boutons">
          <button
            type="button"
            className="bouton secondaire"
            onClick={onAnnuler}
            disabled={envoi}
          >
            Annuler
          </button>
          <button className="bouton" type="submit" disabled={!pret || envoi}>
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </>
  );
}
